import { createFileRoute, Outlet, Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  BarChart3,
  Shield,
  FolderKanban,
  Wrench,
  FileText,
  Inbox,
  Settings as SettingsIcon,
  LogOut,
  Menu,
  X,
  MessageSquare,
  Camera,
  Images,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { sb } from "@/lib/sb";

export const Route = createFileRoute("/_authenticated/dashboard")({
  component: DashboardLayout,
});

const nav = [
  { to: "/dashboard/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/dashboard/admin", label: "Admin", icon: Shield },
  { to: "/dashboard/projects", label: "Projects", icon: FolderKanban },
  { to: "/dashboard/services", label: "Services", icon: Wrench },
  { to: "/dashboard/pages", label: "Pages", icon: FileText },
  { to: "/dashboard/site-photos", label: "Site Photos", icon: Camera },
  { to: "/dashboard/image-variants", label: "Image Variants", icon: Images },
  { to: "/dashboard/markup", label: "Markup", icon: MessageSquare },
  { to: "/dashboard/form", label: "Form", icon: Inbox },
  { to: "/dashboard/settings", label: "Settings", icon: SettingsIcon },
] as const;


function DashboardLayout() {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const ctx = Route.useRouteContext() as { user?: { email?: string | null } };
  const user = ctx.user;
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const refresh = async () => {
      const { count } = await sb
        .from("form_submissions")
        .select("*", { count: "exact", head: true })
        .eq("status", "new");
      setUnread(count ?? 0);
    };
    refresh();
    const id = setInterval(refresh, 60000);
    return () => clearInterval(id);
  }, [pathname]);

  const logout = async () => {
    await supabase.auth.signOut();
    navigate({ to: "/auth", replace: true });
  };

  return (
    <div className="min-h-screen bg-surface-alt flex">
      <aside
        className={`fixed md:static z-40 inset-y-0 left-0 w-64 bg-card border-r border-border flex flex-col transform transition-transform ${
          open ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        }`}
      >
        <div className="px-5 py-5 border-b border-border flex items-center justify-between">
          <div>
            <p className="font-display font-bold text-ink">Homestead Construction Admin</p>
            <p className="text-xs text-muted-foreground">Dashboard</p>
          </div>
          <button className="md:hidden" onClick={() => setOpen(false)}>
            <X size={18} />
          </button>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-auto">
          {nav.map((n) => {
            const active = pathname === n.to || pathname.startsWith(n.to + "/");
            return (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  active
                    ? "bg-brand/10 text-brand"
                    : "text-muted-foreground hover:text-foreground hover:bg-background"
                }`}
              >
                <n.icon size={16} />
                <span className="flex-1">{n.label}</span>
                {n.to === "/dashboard/form" && unread > 0 && (
                  <span className="ml-auto text-xs bg-brand text-primary-foreground rounded-full px-2 py-0.5">
                    {unread}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b border-border flex items-center px-4 gap-3 bg-card/50">
          <button className="md:hidden" onClick={() => setOpen(true)}>
            <Menu size={18} />
          </button>
          <div className="flex-1 text-sm text-muted-foreground truncate">{user?.email}</div>
          <button
            onClick={logout}
            className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-2"
          >
            <LogOut size={14} /> <span className="hidden sm:inline">Logout</span>
          </button>
        </header>
        <main className="flex-1 p-4 md:p-6 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
